import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useBoundStore } from '@/store';
import { queryKeys } from '@/api/queryKeys';
import { ThoughtSpotSdkAuthStatus } from '@/store/slices/createThoughtspotSlice';
import { ClusterBanner } from '@/types/thoughtspot';
import { CRITICAL_QUERY_RETRY_COUNT, MAINTENANCE_BANNER } from '@/constants/queries';

interface UseThoughtSpotBannerQueryParams {
  isAuthenticated: boolean;
  authStatus: ThoughtSpotSdkAuthStatus;
  options?: Partial<UseQueryOptions<ClusterBanner, Error, ClusterBanner>>;
}

export async function getThoughtSpotClusterBanner(): Promise<ClusterBanner> {
  const thoughtspotUrl = useBoundStore.getState().getCurrentThoughtspotUrl();

  const response = await fetch(`${thoughtspotUrl}api/rest/2.0/system/banner`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch banner status: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return data as ClusterBanner;
}

export function useThoughtSpotBannerQuery({ isAuthenticated, authStatus, options }: UseThoughtSpotBannerQueryParams) {
  const selectedRegion = useBoundStore((state) => state.selectedRegion);

  return useQuery<ClusterBanner, Error>({
    // including selectedRegion to ensure cache invalidation on region change
    queryKey: queryKeys.thoughtspotBanner.byRegion(selectedRegion),
    queryFn: getThoughtSpotClusterBanner,
    enabled: isAuthenticated && authStatus === ThoughtSpotSdkAuthStatus.Authenticated,
    refetchInterval: MAINTENANCE_BANNER.REFETCH_INTERVAL_MS,
    staleTime: MAINTENANCE_BANNER.REFETCH_INTERVAL_MS,
    gcTime: MAINTENANCE_BANNER.REFETCH_INTERVAL_MS,
    retry: CRITICAL_QUERY_RETRY_COUNT,
    ...options, // Spread any additional options
  });
}
